/**
 * Build a session-clock snapshot from server topic_timing (applyTopicTimingRecords).
 * Used when the primary trainer leaves and LiveKit clock packets stop.
 */

import type { LiveSessionClockMessage } from "../liveSessionClockSync";
import { LIVE_SESSION_CLOCK_TYPE } from "../liveSessionClockSync";
import type { SessionTopicPlan } from "./types";

export function syncedClockFromTopicPlan(
  topics: SessionTopicPlan[],
  projectedEndTime = "",
): LiveSessionClockMessage | null {
  if (!topics.length) return null;

  const completedTopicIds: string[] = [];
  const skippedTopicIds: string[] = [];
  const topicActualDurationsSec: Record<string, number> = {};
  let currentTopicId: string | null = null;
  let currentSubtopicId: string | null = null;
  let currentSubtopicActualStartAt: string | null = null;
  let topicStarted = false;
  let hasProgress = false;

  for (const t of topics) {
    const subs = t.subtopics || [];
    if (subs.length) {
      for (const s of subs) {
        if (s.skipped) {
          hasProgress = true;
          completedTopicIds.push(s.id);
          skippedTopicIds.push(s.id);
        } else if (s.completed) {
          hasProgress = true;
          completedTopicIds.push(s.id);
          if (s.actualDurationSec != null) {
            topicActualDurationsSec[s.id] = s.actualDurationSec;
          }
        } else if (!currentSubtopicId && s.actualStartAt) {
          topicStarted = true;
          hasProgress = true;
          currentTopicId = t.id;
          currentSubtopicId = s.id;
          currentSubtopicActualStartAt = s.actualStartAt;
        }
      }
      if (t.skipped) {
        hasProgress = true;
        skippedTopicIds.push(t.id);
        completedTopicIds.push(t.id);
      } else if (t.completed) {
        hasProgress = true;
        completedTopicIds.push(t.id);
        if (t.actualDurationSec != null) {
          topicActualDurationsSec[t.id] = t.actualDurationSec;
        }
      }
    } else if (t.skipped) {
      hasProgress = true;
      skippedTopicIds.push(t.id);
      completedTopicIds.push(t.id);
    } else if (t.completed) {
      hasProgress = true;
      completedTopicIds.push(t.id);
      if (t.actualDurationSec != null) {
        topicActualDurationsSec[t.id] = t.actualDurationSec;
      }
    } else if (!currentTopicId && t.actualStartAt) {
      topicStarted = true;
      hasProgress = true;
      currentTopicId = t.id;
    }
  }

  if (!hasProgress && !topicStarted) return null;

  return {
    type: LIVE_SESSION_CLOCK_TYPE,
    v: 1,
    projectedEndTime,
    qaPaused: false,
    topicTimeBonusSec: 0,
    pauseAccumulatedSec: 0,
    completedTopicIds,
    skippedTopicIds,
    topicActualDurationsSec,
    topicElapsedSec: 0,
    subtopicElapsedSec: 0,
    sessionElapsedSec: 0,
    topicStarted,
    currentTopicId,
    currentSubtopicId,
    currentSubtopicActualStartAt,
  };
}

/** Prefer API topic_timing completions when live clock is missing or stale. */
export function mergeSessionClockWithTopicTiming(
  live: LiveSessionClockMessage | null,
  fallback: LiveSessionClockMessage | null,
): LiveSessionClockMessage | null {
  if (!live) return fallback;
  if (!fallback) return live;

  const mergedCompleted = [
    ...new Set([
      ...(live.completedTopicIds ?? []),
      ...(fallback.completedTopicIds ?? []),
    ]),
  ];
  const mergedSkipped = [
    ...new Set([
      ...(live.skippedTopicIds ?? []),
      ...(fallback.skippedTopicIds ?? []),
    ]),
  ];

  const liveSubId = live.currentSubtopicId
    ? String(live.currentSubtopicId)
    : null;
  const fallbackSubId = fallback.currentSubtopicId
    ? String(fallback.currentSubtopicId)
    : null;
  // Never attach a prior subtopic's start ISO onto a newer live cursor —
  // that makes student "Time taken" jump by the first subtopic's elapsed.
  const fallbackStartUsable =
    Boolean(fallback.currentSubtopicActualStartAt) &&
    (!liveSubId || !fallbackSubId || liveSubId === fallbackSubId);

  return {
    ...live,
    completedTopicIds: mergedCompleted,
    skippedTopicIds: mergedSkipped,
    topicActualDurationsSec: {
      ...(fallback.topicActualDurationsSec ?? {}),
      ...(live.topicActualDurationsSec ?? {}),
    },
    topicStarted: live.topicStarted || fallback.topicStarted,
    currentTopicId: live.currentTopicId ?? fallback.currentTopicId,
    currentSubtopicId: live.currentSubtopicId ?? fallback.currentSubtopicId,
    currentSubtopicActualStartAt:
      live.currentSubtopicActualStartAt ??
      (fallbackStartUsable ? fallback.currentSubtopicActualStartAt : null),
    projectedEndTime: live.projectedEndTime || fallback.projectedEndTime,
    pauseAccumulatedSec: Math.max(
      live.pauseAccumulatedSec ?? 0,
      fallback.pauseAccumulatedSec ?? 0,
    ),
  };
}
